/**
 * The ⓘ beside the People Lists title — what "candidate" and "person" mean.
 *
 * The rename from "Contact Lists" made the distinction load-bearing. A list
 * holds PEOPLE, and a person on it may be nobody in particular yet: scraped
 * off LinkedIn, never exported, unknown to Thrive. A CANDIDATE is narrower —
 * a person who is in a search. Jared restated it himself on the 2026-08-27
 * call: "candidates or candidacies are people who are in a search and thrive."
 *
 * It sits on the list HEADER rather than a settings page because the header is
 * where the ambiguity actually bites: the reader has just been told the page
 * is about "people" and the table below it talks about candidates.
 *
 * Reuses `IconPopover` (click-to-open disclosure, Escape restores focus)
 * instead of the hover `Tooltip` for the same reason `ImportStatsPopover`
 * does: hover content is `pointer-events-none`, so it cannot be selected,
 * copied, or reached on touch — wrong for two definitions a reader wants to
 * sit with.
 */
import { Info } from "lucide-react";

import { IconPopover } from "../import-detail/IconPopover";

function Term({ term, meaning }: { term: string; meaning: string }) {
  return (
    <span className="flex flex-col">
      <span className="font-semibold text-text">{term}</span>
      <span className="text-text-secondary">{meaning}</span>
    </span>
  );
}

export function VocabularyPopover() {
  return (
    <IconPopover
      label="What candidate and person mean"
      triggerTestId="people-list-vocabulary-trigger"
      panelTestId="people-list-vocabulary"
      panelClassName="max-w-[300px]"
      icon={<Info aria-hidden="true" className="h-4 w-4" />}
    >
      <span className="flex flex-col gap-2 text-left text-caption">
        <Term term="candidate" meaning="A person who is in a search." />
        <Term
          term="person"
          meaning="Anyone on a people list. They may or may not exist in Thrive yet."
        />
      </span>
    </IconPopover>
  );
}
